import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import API, { apiError } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Loader2, MessageSquarePlus, Inbox, ChevronRight, ArrowLeft } from "lucide-react";
import { StatusBadge, formatDate, STATUS_META } from "@/lib/qa";

// Query-param aliases used by the dashboard KPI cards.
//   ?stato=tutte    → show everything
//   ?stato=attesa   → received + in_progress (matches "In attesa di risposta")
//   ?stato=risposte → answered + published  (matches "Risposte ricevute")
const GROUP_ALIASES = {
    tutte: { key: "all", label: "Tutte" },
    attesa: { key: "waiting", label: "In attesa di risposta" },
    risposte: { key: "answered_all", label: "Risposte ricevute" },
};

const GROUP_STATUSES = {
    waiting: ["received", "in_progress"],
    answered_all: ["answered", "published"],
};

export default function MyRequests() {
    const [searchParams, setSearchParams] = useSearchParams();
    const initial = useMemo(() => {
        const alias = (searchParams.get("stato") || "").toLowerCase();
        return GROUP_ALIASES[alias]?.key || "all";
    }, [searchParams]);

    const [items, setItems] = useState([]);
    const [filter, setFilter] = useState(initial);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");

    // Re-sync when the URL query param changes (e.g. user clicks another KPI card).
    useEffect(() => { setFilter(initial); }, [initial]);

    // Fetch strategy: group filters need the full list (multi-status), single
    // statuses can be filtered server-side. Keep both paths simple.
    useEffect(() => {
        setLoading(true);
        const isGroup = filter === "waiting" || filter === "answered_all";
        const params = !isGroup && filter !== "all" ? { status: filter } : {};
        API.get("/questions/me", { params })
            .then(({ data }) => {
                if (isGroup) {
                    const allowed = new Set(GROUP_STATUSES[filter]);
                    setItems(data.filter(q => allowed.has(q.status)));
                } else {
                    setItems(data);
                }
            })
            .catch(e => setError(apiError(e)))
            .finally(() => setLoading(false));
    }, [filter]);

    // Keep the URL in sync so the current filter is bookmarkable / shareable.
    const onFilterChange = (v) => {
        setFilter(v);
        const aliasEntry = Object.entries(GROUP_ALIASES).find(([, val]) => val.key === v);
        if (aliasEntry) {
            setSearchParams({ stato: aliasEntry[0] }, { replace: true });
        } else if (v === "all") {
            setSearchParams({}, { replace: true });
        } else {
            setSearchParams({ status: v }, { replace: true });
        }
    };

    return (
        <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8 py-10">
            <Button asChild variant="ghost" size="sm" className="-ml-2 mb-3 text-muted-foreground" data-testid="myreq-back">
                <Link to="/dashboard"><ArrowLeft className="size-4 mr-1" /> Torna alla dashboard</Link>
            </Button>
            <div className="flex items-center justify-between mb-6">
                <div>
                    <h1 className="font-serif text-3xl font-semibold" data-testid="myreq-title">Le mie richieste</h1>
                    <p className="text-muted-foreground text-sm mt-1">Storico e stato di tutte le tue domande.</p>
                </div>
                <Button asChild data-testid="myreq-new"><Link to="/dashboard/nuova"><MessageSquarePlus className="size-4 mr-2" />Nuova domanda</Link></Button>
            </div>

            <div className="mb-4 max-w-xs">
                <Select value={filter} onValueChange={onFilterChange}>
                    <SelectTrigger data-testid="myreq-filter"><SelectValue /></SelectTrigger>
                    <SelectContent>
                        <SelectItem value="all">Tutte</SelectItem>
                        <SelectItem value="waiting">In attesa di risposta</SelectItem>
                        <SelectItem value="answered_all">Risposte ricevute</SelectItem>
                        {Object.entries(STATUS_META).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
                    </SelectContent>
                </Select>
            </div>

            {error && <Alert variant="destructive" className="mb-4"><AlertDescription>{error}</AlertDescription></Alert>}

            {loading ? (
                <div className="text-center py-16"><Loader2 className="size-6 mx-auto animate-spin text-muted-foreground" /></div>
            ) : items.length === 0 ? (
                <Card className="border-dashed border-2 bg-muted/30" data-testid="myreq-empty">
                    <CardContent className="py-16 text-center">
                        <Inbox className="size-12 mx-auto text-muted-foreground/40" />
                        <h2 className="mt-5 font-serif text-2xl font-semibold">Non hai ancora fatto domande</h2>
                        <p className="mt-2 text-muted-foreground">Il primo passo è il più importante.</p>
                        <Button asChild className="mt-6"><Link to="/dashboard/nuova">Fai la tua prima domanda</Link></Button>
                    </CardContent>
                </Card>
            ) : (
                <div className="space-y-3" data-testid="myreq-list">
                    {items.map(q => (
                        <Link key={q.id} to={`/dashboard/richieste/${q.id}`} data-testid={`myreq-item-${q.id}`}
                            className="block rounded-xl border border-slate-200 bg-white p-5 hover:border-primary/30 hover:shadow-md transition">
                            <div className="flex items-start justify-between gap-3">
                                <div className="min-w-0">
                                    <div className="flex items-center gap-2 mb-1">
                                        <span className="text-xs font-semibold text-primary uppercase tracking-widest">Domanda</span>
                                        <StatusBadge status={q.status} />
                                    </div>
                                    <div className="font-semibold text-slate-900 truncate">{q.title}</div>
                                    <div className="mt-1 text-xs text-muted-foreground">Inviata il {formatDate(q.created_at)}</div>
                                </div>
                                <ChevronRight className="size-5 text-muted-foreground shrink-0" />
                            </div>
                        </Link>
                    ))}
                </div>
            )}
        </div>
    );
}
